import uuidv1 from "uuid/v1";
import resolveFragment from "./fragment";
import resolveShared from "./shared";

const cachedByID = {};
const cachedByPlayableID = {};

function setCachedItem(item) {
  setCachedByPlayableID(item);
  setCachedByID(item);
}

function setCachedByPlayableID(item) {
  const playableID = playableIDForItem(item);
  if (playableID) {
    const toBeCached = { ...item };
    delete toBeCached.id;
    cachedByPlayableID[playableID] = toBeCached;
  }
}

function setCachedByID(item) {
  const { id } = item;
  if (id) {
    cachedByID[id] = { ...item };
  }
}

function findCachedByPlayableID(item) {
  const playableID = playableIDForItem(item);
  if (playableID && cachedByPlayableID[playableID]) {
    return cachedByPlayableID[playableID];
  }
  return null;
}

function findCachedByID(item) {
  const { id } = item;
  if (id && cachedByID[id]) {
    return cachedByID[id];
  }
  return null;
}

function removeTitleRepetition(item) {
  if (item.data) {
    const titleMatcher = new RegExp(`( (- )?)?${item.data.subtitle}$`);
    const updatedTitle = item.data.title.replace(titleMatcher, "");
    return {
      ...item,
      data: {
        ...item.data,
        title: updatedTitle
      }
    };
  } else {
    return item;
  }
}

function playableIDForItem(item) {
  // For shared items, we need to differentiate them locally by their attributes
  if (item) {
    return item.data && item.data.start_offset != null
      ? `${item.player_item_id}_${item.data.start_offset}_${item.data.end_offset}`
      : item.player_item_id;
  } else {
    return null;
  }
}

const itemResolverMapping = {
  broadcast_episode: resolveFragment,
  clip: resolveFragment
};

async function resolvePlayerItem(item) {
  item.playable_id = playableIDForItem(item);
  item = removeTitleRepetition(item);
  if (item.type === "shared_audio_item") {
    item = await resolveShared(item);
  }
  // Extra type after shared resolution
  let { type } = item;
  // Special-case clip resolution as they can be fragments *or* audio files
  if (type === "clip" && item.data.audio_file) {
    type = "audio_file";
  }
  const resolver = itemResolverMapping[type];
  if (resolver) {
    return resolver(item);
  }
  return item;
}

export default function decoratePlayerItem(
  item,
  { refreshID } = { refreshID: false }
) {
  return new Promise(resolve => {
    const cachedWithID = findCachedByID(item);
    if (!refreshID && cachedWithID) {
      // If matching existing id, then we can return the full value
      resolve(cachedWithID);
    } else {
      const cachedWithPlayableID = findCachedByPlayableID(item);
      if (cachedWithPlayableID) {
        // Use most values but add a unique ID
        const newItem = { ...cachedWithPlayableID, id: uuidv1() };
        setCachedItem(newItem);
        resolve(newItem);
      } else {
        // Create an ID before any resolution
        if (item.id == null) {
          item = { ...item, id: uuidv1() };
        }
        resolvePlayerItem(item).then(resolvedItem => {
          setCachedItem(resolvedItem);
          resolve(resolvedItem);
        });
      }
    }
  });
}
